import React, { createContext, useContext, useMemo, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { ApiClient } from './client';
import { TokenManager } from '../../auth/tokenManager';
import { apiRequest, loginRequest } from '../../authConfig';

// Extend Window interface for E2E test flag
declare global {
  interface Window {
    __E2E_TEST__?: boolean;
  }
}

interface ApiProviderProps {
  children: React.ReactNode;
  baseURL?: string;
}

interface ApiContextValue {
  client: ApiClient;
}

const ApiContext = createContext<ApiContextValue | null>(null);

export function ApiProvider({ children, baseURL = '/api' }: ApiProviderProps) {
  const { instance, accounts } = useMsal();

  // Check if we're in E2E test mode. Gated to non-production builds so the
  // tokenless-request bypass is dead code in prod and cannot be triggered by
  // setting the flag in a production browser session.
  const isE2EMode =
    typeof window !== 'undefined' &&
    window.__E2E_TEST__ === true &&
    process.env.NODE_ENV !== 'production';

  // Initialize TokenManager with MSAL instance
  const tokenManager = useMemo(
    () => TokenManager.getInstance(instance),
    [instance]
  );

  // Clear token cache on logout
  useEffect(() => {
    if (accounts.length === 0 && !isE2EMode) {
      tokenManager.clearCache();
    }
  }, [accounts, tokenManager, isE2EMode]);

  const client = useMemo(() => {
    // In E2E mode, bypass token fetching entirely
    const getToken = isE2EMode
      ? undefined
      : async () => {
          if (!accounts[0]) {
            throw new Error('No active account');
          }

          // Guard: an empty scopes array (REACT_APP_AZURE_CLIENT_ID and
          // REACT_APP_API_SCOPE both unset) makes MSAL throw an opaque error.
          // Fail with an explicit configuration message instead.
          if (apiRequest.scopes.length === 0) {
            throw new Error(
              'API scope is not configured: set REACT_APP_AZURE_CLIENT_ID (or REACT_APP_API_SCOPE) so the SPA can request an api://<clientId> token.'
            );
          }

          try {
            // Use TokenManager to prevent race conditions. Request the API
            // audience scope (api://<clientId>/acc): a Graph/OIDC-scoped token
            // has the wrong `aud` for our API and — for Microsoft Graph tokens
            // specifically, which carry a nonce in the JWT header — also fails
            // signature validation, which the API surfaces as 401
            // "invalid signature".
            return await tokenManager.getToken({
              scopes: apiRequest.scopes,
              account: accounts[0],
            });
          } catch (error) {
            if (error instanceof InteractionRequiredAuthError) {
              console.log(
                '[ApiProvider] Interactive login required, redirecting...'
              );
              instance.loginRedirect(loginRequest);
              throw error;
            }
            throw error;
          }
        };

    return new ApiClient({
      baseURL,
      getToken,
      onAuthError: isE2EMode
        ? undefined
        : () => {
            tokenManager.clearCache();
            instance.loginRedirect(loginRequest);
          },
    });
  }, [instance, accounts, baseURL, tokenManager, isE2EMode]);

  return (
    <ApiContext.Provider value={{ client }}>{children}</ApiContext.Provider>
  );
}

export function useApi() {
  const context = useContext(ApiContext);
  if (!context) {
    throw new Error('useApi must be used within an ApiProvider');
  }
  return context.client;
}
